import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext.jsx';
import { useSettings } from '../context/SettingsContext.jsx';
import { readFocusRaw, applyRemoteFocus, readTimerRaw, applyRemoteTimer } from '../lib/focusLog.js';
import { readPlacement, writePlacement } from '../data/navTabs.js';

// Watchlist (markets) manual order lives in localStorage; we piggyback it on
// the same ui_prefs blob so it follows the user across devices.
const readWlOrder = () => {
  try {
    const c = JSON.parse(localStorage.getItem('wl_order_crypto') || '[]');
    const f = JSON.parse(localStorage.getItem('wl_order_currency') || '[]');
    return { crypto: Array.isArray(c) ? c : [], currency: Array.isArray(f) ? f : [] };
  } catch { return { crypto: [], currency: [] }; }
};
const writeWlOrder = (w) => {
  try {
    if (Array.isArray(w?.crypto)) localStorage.setItem('wl_order_crypto', JSON.stringify(w.crypto));
    if (Array.isArray(w?.currency)) localStorage.setItem('wl_order_currency', JSON.stringify(w.currency));
    window.dispatchEvent(new Event('wl_order_changed'));
  } catch { /* ignore */ }
};

// Syncs appearance prefs (active theme, pet, saved custom themes, fonts, sizes,
// manual colour overrides) with the server so they follow the user across
// devices. localStorage stays the instant cache; the server is the source of
// truth on a fresh load. Renders nothing.
//
// Flow:
//   • on mount → GET /settings. If the server has prefs, hydrate them; if empty,
//     seed it with whatever is local.
//   • on any appearance change → debounced PUT /settings.
//   • echoes are suppressed by comparing against the last synced snapshot.
export default function ThemeSync() {
  const { apiFetch, token } = useAuth();
  const s = useSettings();
  const { theme, pet, customThemes, font, fontScale, customColor, customText, customCard, hydratePrefs } = s;

  const ready = useRef(false);
  const lastSynced = useRef(null);
  // Bumped when the user reorders markets assets → triggers a debounced save.
  const [wlTick, setWlTick] = useState(0);
  useEffect(() => {
    const h = () => setWlTick(n => n + 1);
    window.addEventListener('wl_order_changed', h);
    return () => window.removeEventListener('wl_order_changed', h);
  }, []);
  // Bumped when a focus session is logged (or the ledger merges) → debounced save.
  const [focusTick, setFocusTick] = useState(0);
  useEffect(() => {
    const h = () => setFocusTick(n => n + 1);
    window.addEventListener('focus_log_changed', h);
    window.addEventListener('focus_timer_changed', h);
    window.addEventListener('placement_changed', h);
    return () => { window.removeEventListener('focus_log_changed', h); window.removeEventListener('focus_timer_changed', h); window.removeEventListener('placement_changed', h); };
  }, []);

  const snapshot = () => JSON.stringify({ theme, pet, customThemes, font, fontScale, customColor, customText, customCard, wlOrder: readWlOrder(), focusLog: readFocusRaw(), focusTimer: readTimerRaw(), placement: readPlacement() });

  // Initial load (re-runs if the account changes, e.g. login/logout).
  useEffect(() => {
    if (!token) { ready.current = false; return; }
    let alive = true;
    ready.current = false;
    let retryT = null;
    const attempt = () => {
      apiFetch('/settings')
        .then(r => (r && r.ok) ? r.json() : Promise.reject(new Error('settings unavailable')))
.then(d => {
        if (!alive) return;
        const p = (d && d.ui_prefs) || {};
        const hasServer = p && typeof p === 'object' && Object.keys(p).length > 0;
        if (hasServer) {
          hydratePrefs(p);
          if (p.wlOrder && typeof p.wlOrder === 'object') writeWlOrder(p.wlOrder);
          if (p.focusLog && typeof p.focusLog === 'object') applyRemoteFocus(p.focusLog);
          if (p.focusTimer && typeof p.focusTimer === 'object') applyRemoteTimer(p.focusTimer);
          if (p.placement && typeof p.placement === 'object' && JSON.stringify(p.placement) !== JSON.stringify(readPlacement())) writePlacement(p.placement);
          // Record the post-hydrate state so the save effect doesn't echo it back.
          lastSynced.current = JSON.stringify({
            theme: typeof p.theme === 'string' ? p.theme : theme,
            pet: typeof p.pet === 'string' ? p.pet : pet,
            customThemes: (p.customThemes && typeof p.customThemes === 'object') ? p.customThemes : customThemes,
            font: typeof p.font === 'string' ? p.font : font,
            fontScale: typeof p.fontScale === 'number' ? p.fontScale : fontScale,
            customColor: typeof p.customColor === 'string' ? p.customColor : customColor,
            customText: typeof p.customText === 'string' ? p.customText : customText,
            customCard: typeof p.customCard === 'string' ? p.customCard : customCard,
            wlOrder: readWlOrder(),
            focusLog: readFocusRaw(),
            focusTimer: readTimerRaw(),
            placement: readPlacement(),
          });
        } else {
          // Server is empty — seed it with the current local appearance.
          const local = snapshot();
          lastSynced.current = local;
          apiFetch('/settings', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: local }).catch(() => {});
        }
        ready.current = true;
      })
        .catch(() => {
          if (!alive) return;
          // GET failed (offline, deploy 502, expired token mid-outage). Crucially this
          // is NOT "server has no prefs": enabling saves now would overwrite the
          // account blob with this device's local state (fresh device = wipe).
          // Keep saves blocked and retry until a hydrate actually succeeds.
          retryT = setTimeout(attempt, 20000);
        });
    };
    attempt();
    const onUp = () => { if (alive && !ready.current) attempt(); };
    window.addEventListener('online', onUp);
    return () => { alive = false; window.removeEventListener('online', onUp); if (retryT) clearTimeout(retryT); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Persist on change (debounced).
  useEffect(() => {
    if (!ready.current) return;
    const json = snapshot();
    if (json === lastSynced.current) return;
    const id = setTimeout(() => {
      apiFetch('/settings', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: json })
        .then(() => { lastSynced.current = json; })
        .catch(() => {});
    }, 700);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, pet, customThemes, font, fontScale, customColor, customText, customCard, wlTick, focusTick]);

  return null;
}
